
const BASE_URL = 'https://api.coinnexs.com/api';

export interface AdminKycRequest {
    id: string;
    name: string;
    email: string;
    documentType: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    submittedAt: string;
}

export interface AdminKycListResponse {
    success: boolean;
    data: AdminKycRequest[];
    message?: string;
}

export interface AdminKycDetail {
    id: string;
    name: string;
    email: string;
    documentType: string;
    fileKey: string;
    submittedAt: string;
    status?: 'PENDING' | 'APPROVED' | 'REJECTED';
    remarks?: string | null;
}

export interface AdminKycDetailResponse {
    success: boolean;
    data: AdminKycDetail;
    message?: string;
}

export interface FileViewResponse {
    url: string;
}

export interface AdminKycActionResponse {
    success: boolean;
    status: 'APPROVED' | 'REJECTED';
    message?: string;
}

class AdminKycService {
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const token = localStorage.getItem('coinnexs_token');
        const headers: Record<string, string> = {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...((options.body instanceof FormData) ? {} : { 'Content-Type': 'application/json' }),
        };

        try {
            const response = await fetch(`${BASE_URL}${endpoint}`, {
                ...options,
                headers: {
                    ...headers,
                    ...(options.headers as Record<string, string>),
                },
            });

            const data = await response.json();
            return data as T;
        } catch (error) {
            console.error(`Admin KYC API Error (${endpoint}):`, error);
            return {
                success: false,
                message: 'Internal protocol error. Please try again later.',
            } as unknown as T;
        }
    }

    async getKycRequests(params: { status?: string; page?: number; limit?: number } = {}): Promise<AdminKycListResponse> {
        const query = new URLSearchParams();
        if (params.status) query.append('status', params.status);
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());

        const queryString = query.toString();
        return this.request<AdminKycListResponse>(`/admin/kyc${queryString ? `?${queryString}` : ''}`);
    }

    async getKycDetail(kycId: string): Promise<AdminKycDetailResponse> {
        return this.request<AdminKycDetailResponse>(`/admin/kyc/${kycId}`);
    }

    async getFileView(fileKey: string): Promise<FileViewResponse> {
        return this.request<FileViewResponse>(`/files/view?key=${encodeURIComponent(fileKey)}`);
    }

    async approveKyc(kycId: string, remarks: string = "Documents verified successfully"): Promise<AdminKycActionResponse> {
        return this.request<AdminKycActionResponse>(`/admin/kyc/${kycId}/approve`, {
            method: 'POST',
            body: JSON.stringify({ remarks }),
        });
    }

    async rejectKyc(kycId: string, remarks: string): Promise<AdminKycActionResponse> {
        return this.request<AdminKycActionResponse>(`/admin/kyc/${kycId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ remarks }),
        });
    }
}

export const adminKycService = new AdminKycService();
